import { ChevronDown } from 'lucide-react';
import type { SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type DropdownOption = {
  value: string;
  label: string;
  /** Group label — adjacent options sharing a group are bunched into an optgroup. */
  group?: string;
  disabled?: boolean;
};

type DropdownSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'children' | 'size'
> & {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  /** Hex/rgb color for an 8px circle rendered inside the pill, left of the label. */
  leadingDotColor?: string;
};

const sizeClass = {
  sm: 'h-9 text-[13px] pl-3 pr-8',
  md: 'h-11 text-[15px] pl-4 pr-9',
} as const;
const sizeClassWithDot = {
  sm: 'h-9 text-[13px] pl-8 pr-8',
  md: 'h-11 text-[15px] pl-9 pr-9',
} as const;

/**
 * Wraps a styled native <select> so iOS/Android show their native picker
 * (best UX on touch devices). Adjacent options sharing a `group` are
 * grouped into a single <optgroup>.
 */
export function DropdownSelect({
  options,
  value,
  onChange,
  placeholder,
  size = 'md',
  className,
  disabled,
  leadingDotColor,
  ...rest
}: DropdownSelectProps) {
  // Bucket adjacent options that share the same group label.
  const groups: Array<{ label: string | null; items: DropdownOption[] }> = [];
  for (const opt of options) {
    const groupLabel = opt.group ?? null;
    const last = groups[groups.length - 1];
    if (last && last.label === groupLabel) {
      last.items.push(opt);
    } else {
      groups.push({ label: groupLabel, items: [opt] });
    }
  }

  return (
    <div
      className={cn(
        'relative inline-flex items-center w-full rounded-button',
        'bg-surface-secondary text-text-primary',
        disabled && 'opacity-40 pointer-events-none',
        className,
      )}
    >
      {leadingDotColor ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
          style={{ backgroundColor: leadingDotColor }}
        />
      ) : null}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'appearance-none bg-transparent w-full font-medium',
          'focus:outline-none focus:ring-2 focus:ring-accent/40 rounded-button',
          leadingDotColor ? sizeClassWithDot[size] : sizeClass[size],
        )}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {groups.map((g, i) =>
          g.label ? (
            <optgroup key={`${g.label}-${i}`} label={g.label}>
              {g.items.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                >
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ) : (
            g.items.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))
          ),
        )}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-3 text-text-tertiary"
        strokeWidth={1.5}
      />
    </div>
  );
}
